// Карточка платформы: паспорт, все известные значения — и у КАЖДОГО источник.
//
// «С указанием источника» — не украшение раздела, а его смысл: про один буй
// отвечают разные каналы (INSTAC с контролем качества и задержкой, NDBC
// минутами и без него), и молчаливое слияние спрятало бы, кто именно это
// сказал. Поэтому источник стоит в строке значения, а не один раз в шапке.

import type { BuoyCard as BuoyCardData, BuoyResidual, BuoyValue } from '../api'
import { useT } from '../i18n'
import { freshnessColor, typeLabel } from './buoyLayer'
import type { BuoyFreshness } from './buoyLayer'
import { sparklineBounds, sparklinePath } from './sparkline'

interface Props {
  data: BuoyCardData
  onClose: () => void
}

const SPARK_W = 132
const SPARK_H = 22

function fmt(value: number | null | undefined, digits = 2): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return Number(value).toFixed(digits)
}

/** Подпись глубины. Отрицательная — это ВЫСОТА над водой (анемометр, датчик
 *  воздуха), и молчать об этом нельзя: «−4 м» читается как под водой. */
function depthLabel(depth: number | null, t: (k: string, p?: Record<string, string | number>) => string): string {
  if (depth == null) return '—'
  if (depth < 0) return t('buoys.card.aboveSea', { m: Math.abs(depth).toFixed(1) })
  return t('buoys.card.belowSea', { m: depth.toFixed(1) })
}

export function BuoyCard({ data, onClose }: Props) {
  const t = useT()
  const p = data.platform
  const state = (p.freshness === 'retired' ? 'stale' : p.freshness) as BuoyFreshness

  return (
    <div className="console-scroll pointer-events-auto max-h-[70vh] w-[340px] overflow-y-auto rounded-xl border border-hull bg-deep p-3 shadow-console">
      <div className="flex items-start gap-2">
        <span
          aria-hidden
          className="mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ background: freshnessColor(state) }}
        />
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-foam">
            {p.name || p.platform_id}
          </div>
          <div className="truncate text-[12px] text-mist">
            {typeLabel(p.platform_type ?? '')} · {p.platform_id}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded px-1 text-mist hover:text-foam"
          aria-label={t('common.close')}
        >
          ×
        </button>
      </div>

      <dl className="mt-2 space-y-1 border-t border-hull-hair pt-2 text-[12px]">
        <Row label={t('buoys.card.institution')} value={p.institution ?? '—'} />
        <Row label={t('buoys.card.wmo')} value={p.wmo_code ?? '—'} />
        <Row
          label={t('buoys.card.lastObs')}
          value={
            p.age_h == null
              ? t('buoys.freshness.unknown')
              : t('buoys.card.hoursAgo', { h: p.age_h.toFixed(1) })
          }
        />
        <Row label={t('buoys.card.channels')} value={p.sources.join(', ') || '—'} />
      </dl>

      {data.hydrology_hidden && (
        <div className="mt-2 rounded border border-hull-hair px-2 py-1 text-[11px] leading-snug text-faint">
          {t('buoys.card.hydrologyHidden')}
        </div>
      )}

      {data.values.length > 0 && (
        <div className="mt-2 border-t border-hull-hair pt-2">
          <div className="text-[12px] text-faint">{t('buoys.card.measurements')}</div>
          <ul className="mt-1 space-y-2">
            {data.values.map(v => (
              <ValueRow key={`${v.param}-${v.depth_m}-${v.source}`} value={v} series={data.series[v.param]} />
            ))}
          </ul>
        </div>
      )}

      {data.residuals.length > 0 && (
        <div className="mt-2 border-t border-hull-hair pt-2">
          <div className="text-[12px] text-faint">{t('buoys.card.residual')}</div>
          <ul className="mt-1 space-y-1 text-[12px]">
            {data.residuals.map(r => (
              <ResidualRow key={`${r.param}-${r.source}`} residual={r} />
            ))}
          </ul>
          <div className="mt-1 text-[11px] leading-snug text-faint">
            {t('buoys.card.residualHint')}
          </div>
        </div>
      )}

      {data.track.length > 1 && (
        <div className="mt-2 border-t border-hull-hair pt-2 text-[12px] text-mist">
          {t('buoys.card.trackPoints', { n: data.track.length })}
        </div>
      )}
    </div>
  )
}

function ValueRow({
  value,
  series,
}: {
  value: BuoyValue
  series?: Array<{ time: string | null; value: number }>
}) {
  const t = useT()
  const points = series ?? []
  const path = sparklinePath(points, SPARK_W, SPARK_H)
  const bounds = sparklineBounds(points)

  return (
    <li>
      <div className="flex items-baseline justify-between gap-2">
        <span className="min-w-0 truncate text-foam">{value.param}</span>
        <span className="tnum shrink-0 font-medium text-foam">
          {fmt(value.value)} {value.unit ?? ''}
        </span>
      </div>
      <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-faint">
        {/* Источник у КАЖДОГО значения: два канала про один буй — не дубль, а
            факт, и выбирать за человека мы не вправе. */}
        <span className="rounded-full border border-hull px-1.5 py-0.5 leading-none">
          {value.source}
        </span>
        <span>{depthLabel(value.depth_m, t)}</span>
        {value.adjusted && <span title={t('buoys.card.adjustedHint')}>{t('buoys.card.adjusted')}</span>}
        {/* Спор каналов не прячем: значения одного срока разошлись, и человек
            вправе видеть оба, а не выбранное за него. */}
        {value.disagrees && (
          <span className="text-amber-400" title={t('buoys.card.disagreesHint')}>
            {t('buoys.card.disagrees')}
          </span>
        )}
      </div>
      {path && (
        <div className="mt-1">
          <svg width={SPARK_W} height={SPARK_H} className="text-sonar" aria-hidden>
            <path d={path} fill="none" stroke="currentColor" strokeWidth={1.25} />
          </svg>
          {bounds && (
            <div className="tnum text-[10px] text-faint">
              {fmt(bounds.min)} … {fmt(bounds.max)}
            </div>
          )}
        </div>
      )}
    </li>
  )
}

function ResidualRow({ residual }: { residual: BuoyResidual }) {
  const t = useT()
  // Знак сохраняем: «+0,6» и «−0,6» — разные новости о модели, и модуль
  // разницы скрыл бы, в какую сторону она промахивается.
  const sign = residual.diff > 0 ? '+' : ''
  return (
    <li className="flex items-baseline justify-between gap-2">
      <span className="min-w-0 truncate text-mist">{t(`layers.${residual.layer}`)}</span>
      <span className="tnum shrink-0 text-foam">
        {residual.measured.toFixed(2)} − {residual.model.toFixed(2)} ={' '}
        <span className={Math.abs(residual.diff) > 1 ? 'text-amber-400' : ''}>
          {sign}
          {residual.diff.toFixed(2)}
        </span>
      </span>
    </li>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="shrink-0 text-faint">{label}</dt>
      <dd className="min-w-0 truncate text-right font-medium text-foam">{value}</dd>
    </div>
  )
}
